import { computed, defineComponent, ref } from 'vue';
import { useStore } from 'vuex';
import { useDateFormat } from '@/composables/dateFormat';
import { ProjectAction, ProjectGetter } from '@/store/project/project.module';
import { Project, ProjectForm, ApprovementForm } from '@/store/project/project.model';
import { AccountGetter } from '@/store/account/account.module';
import { Role } from '@/store/role/role.model';
import ProjectModalComponent from '@/components/project/create/createTable/projectModal/ProjectModal.vue';
import ApprovementModalComponent from '@/components/project/create/createTable/approvementModal/ApprovementModal.vue';
import ProjectAttachmentModalComponent from '@/components/project/detail/projectAttachmentModal/ProjectAttachmentModal.vue';
import ProjectAttachmentFormComponent from '@/components/project/detail/projectAttachmentForm/ProjectAttachmentForm.vue';
import ProjectImageModalComponent from '../projectImageModal/ProjectImageModal.vue';
import ProjectAttachmentOffcanvasComponent from '../projectAttachmentOffcanvas/ProjectAttachmentOffcanvas.vue';

export default defineComponent({
  name: 'ProjectDetailComponent',
  components: {
    ProjectModalComponent,
    ApprovementModalComponent,
    ProjectAttachmentModalComponent,
    ProjectAttachmentFormComponent,
    ProjectImageModalComponent,
    ProjectAttachmentOffcanvasComponent,
  },
  props: {
    projectId: {
      type: Number,
    }
  },
  emits: [
    'refresh'
  ],
  setup: (props, context) => {
    const dateFormat = useDateFormat();
    const store = useStore();
    const role = computed<Role | undefined>(() => store.getters[AccountGetter.SINGLE_ROLE]);
    const isOrs = computed(() => role.value?.name === 'เจ้าหน้าที่ ส.กกภ.');
    const isMinister = computed(() => role.value?.name === 'ผู้แทน ร.นรม./รมต.นร');
    const isProvince = computed(() => role.value?.name === 'เจ้าหน้าที่ประจำจังหวัด');
    const project = computed<Project>(() => store.getters[ProjectGetter.BY_ID](props.projectId));
    const projectModal = ref<InstanceType<typeof ProjectModalComponent>>();
    const approvementModal = ref<InstanceType<typeof ApprovementModalComponent>>();
    const projectAttachmentModel = ref<InstanceType<typeof ProjectAttachmentModalComponent>>();
    const projectAttachmentForm = ref<InstanceType<typeof ProjectAttachmentFormComponent>>();
    const projectAttachmentOffcanvas = ref<InstanceType<typeof ProjectAttachmentOffcanvasComponent>>();
    const projectImageModal = ref<InstanceType<typeof ProjectImageModalComponent>>();
    const photoPreview = computed<string>(() => project.value?.projectApprovement
      ? project.value.projectApprovement.ministerPhoto
      : require('@/assets/images/user.png'));

    // update project
    const updateProject = (projectData: ProjectForm) => {
      store.dispatch(ProjectAction.UPDATE, {
        id: project.value?.id,
        name: projectData.name,
        description: projectData.description,
        provinceId: project.value?.province.id,
        projectTypeId: projectData.projectTypeId,
        fiscalYearId: project.value?.fiscalYear.id,
        projectApprovement: {
          cabinetMinisterId: project.value?.projectApprovement?.id,
          approvedDate: project.value?.projectApprovement?.approvedDate
        },
        projectBudgets: project.value?.projectBudgets.map(m => {
          return {
            budgetTypeId: m.id,
            amount: m.amount
          };
        })
      }).then(() => context.emit('refresh'));
    };

    // update approvement
    const updateApprovement = (approvementData: ApprovementForm) => {
      store.dispatch(ProjectAction.UPDATE, {
        id: project.value?.id,
        name: project.value?.name,
        description: project.value?.description,
        provinceId: project.value?.province.id,
        projectTypeId: project.value?.projecType.id,
        fiscalYearId: project.value?.fiscalYear.id,
        projectApprovement: {
          cabinetMinisterId: approvementData.cabinetMinisterId,
          approvedDate: approvementData.approvedDate
        },
        projectBudgets: project.value?.projectBudgets.map(m => {
          return {
            budgetTypeId: m.id,
            amount: m.amount
          };
        })
      }).then(() => context.emit('refresh'));
    };

    const editClick = () => {
      projectModal.value?.show();
    };

    const approvementClick = () => {
      approvementModal.value?.show();
    };

    const attachmentClick = () => {
      projectAttachmentModel.value?.show();
    };

    const uploadClick = () => {
      Promise.resolve(projectAttachmentModel.value?.hide())
        .then(() => projectAttachmentForm.value?.show());
    };

    const uploadClose = () => {
      Promise.resolve(projectAttachmentForm.value?.hide())
        .then(() => projectAttachmentModel.value?.show());
    };

    const showAttachment = () => {
      projectAttachmentOffcanvas.value?.show();
    };

    const imageClick = () => {
      projectImageModal.value?.show();
    };

    const thaiDate = (date: Date) =>
      dateFormat.toThaiDate(date, 'long');

    return {
      project,
      photoPreview,
      projectModal,
      approvementModal,
      projectAttachmentModel,
      projectAttachmentForm,
      projectImageModal,
      projectAttachmentOffcanvas,
      isOrs,
      isMinister,
      isProvince,
      updateProject,
      updateApprovement,
      editClick,
      approvementClick,
      attachmentClick,
      uploadClick,
      uploadClose,
      showAttachment,
      imageClick,
      thaiDate,
    };
  }
});
